import React, { useState, useEffect } from 'react';
import { Briefcase, CheckCircle2, Clock, CalendarDays, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Container } from '@/components/ui/layout';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export default function WelcomeSection({ userName, location, experience, tests = [] }) {
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(true);

  useEffect(() => {
    const fetchProfilePicture = async () => {
      try {
        const response = await fetch('/api/get_user_profile_picture', {
          method: 'GET',
        });
        
        if (response.ok) {
          const data = await response.json();
          const rawPhoto = data.profile_photo || null;
          
          let photoUrl = null;
          if (rawPhoto && typeof rawPhoto === 'string') {
            if (rawPhoto.startsWith('data:') || rawPhoto.startsWith('http') || rawPhoto.startsWith('/')) {
              photoUrl = rawPhoto;
            } else {
              photoUrl = `/${rawPhoto.replace(/^\/+/, '')}`;
            }
          }
          
          setProfilePhoto(photoUrl);
        }
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      } finally {
        setPhotoLoading(false);
      }
    };

    fetchProfilePicture();
  }, []);

  const isTestUpcoming = (scheduledTime) => {
    const testTime = new Date(scheduledTime);
    const now = new Date();
    return testTime > now;
  };

  const safeTests = Array.isArray(tests) ? tests : [];

  const completedTests = safeTests.filter(test => test.test_status === 'completed').length;
  const upcomingTests = safeTests.filter(test => 
    test.test_status !== 'completed' && isTestUpcoming(test.scheduled_time)
  ).length;
  const totalTests = safeTests.length;

  // Candidate profile summary — REAL, per-candidate data only (no hardcoded
  // role). Show experience + location when present; if we have neither, render
  // nothing rather than a placeholder like the old "Software Developer".
  const experienceText = experience
    ? (/[a-zA-Z]/.test(String(experience)) ? String(experience) : `${experience} yrs experience`)
    : null;
  const profileBits = [experienceText, location].filter(Boolean);

  return (
    <section className="bg-gradient-to-br from-primary/5 via-background to-primary/5 border-b border-border">
      <Container size="2xl" className="py-6">
        <Card className="border-0 shadow-sm bg-background/80 backdrop-blur-sm">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              {/* Avatar */}
              <div className="relative shrink-0">
                <div className={cn(
                  "w-16 h-16 rounded-xl border-2 border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5",
                  "flex items-center justify-center overflow-hidden shadow-sm"
                )}>
                  {profilePhoto ? (
                    <img 
                      src={profilePhoto} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        setProfilePhoto(null);
                      }}
                    />
                  ) : (
                    <span className="text-2xl font-bold text-primary">
                      {photoLoading ? '...' : (userName ? userName.charAt(0).toUpperCase() : 'U')}
                    </span>
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-background flex items-center justify-center">
                  <CheckCircle2 className="w-3 h-3 text-white" />
                </div>
              </div>
              
              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-1.5">
                  <h1 className="text-xl font-semibold text-foreground truncate leading-normal">
                    Welcome back, {userName || 'User'}
                  </h1>
                  <Badge variant="success" className="w-fit shrink-0">Active</Badge>
                </div>
                {profileBits.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground leading-normal">
                    <Briefcase className="w-4 h-4 shrink-0" />
                    {profileBits.map((bit, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className="text-border shrink-0">•</span>}
                        <span>{bit}</span>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <StatCard 
                  icon={CheckCircle2}
                  value={completedTests}
                  label="Completed"
                  variant="success"
                />
                <StatCard 
                  icon={CalendarDays}
                  value={upcomingTests}
                  label="Upcoming"
                  variant="warning"
                />
                <StatCard 
                  icon={TrendingUp}
                  value={totalTests}
                  label="Total"
                  variant="default"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </Container>
    </section>
  );
}

function StatCard({ icon: Icon, value, label, variant = "default" }) {
  const variants = {
    success: "bg-green-500/10 text-green-600 border-green-500/20",
    warning: "bg-orange-500/10 text-orange-600 border-orange-500/20",
    default: "bg-primary/10 text-primary border-primary/20",
  };

  return (
    <div className={cn(
      "flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-colors",
      variants[variant]
    )}>
      <Icon className="w-4 h-4 shrink-0" />
      <div className="flex flex-col">
        <span className="text-lg font-bold leading-tight">{value}</span>
        <span className="text-xs font-medium opacity-80 leading-normal">{label}</span>
      </div>
    </div>
  );
}